import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { supabase } from '../../../lib/supabaseClient';
import { getCachedSignedUrl } from '../../../utils/storage';
import { useNavigate } from 'react-router-dom';

const makeDataUrl = (b64, mime) => `data:${mime || 'image/png'};base64,${b64}`;

const AICoverImageWizard = ({
  open,
  onClose,
  kind = 'project',
  title,
  description,
  onApply,
}) => {
  const navigate = useNavigate();

  const [promptMode, setPromptMode] = useState('recommend');

  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [personas, setPersonas] = useState([]);
  const [isPersonasLoading, setIsPersonasLoading] = useState(false);
  const [selectedPersonaIds, setSelectedPersonaIds] = useState([]);

  const selectedPersonas = useMemo(
    () => (personas || []).filter((p) => selectedPersonaIds.includes(p?.id)),
    [personas, selectedPersonaIds]
  );

  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectedSuggestion = useMemo(
    () => (suggestions?.[selectedIndex] ? String(suggestions[selectedIndex]) : ''),
    [suggestions, selectedIndex]
  );

  const [editedPrompt, setEditedPrompt] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('');

  const stylePresets = useMemo(
    () => ([
      { id: 'jp_anime', label: '일본 애니메이션', prompt: '일본 애니메이션 풍의 선명한 셀 셰이딩, 감정 표현이 살아있는 캐릭터, 역동적인 구도' },
      { id: 'charlie_brown', label: '찰리 브라운 풍', prompt: '찰리 브라운 같은 신문 연재 만화풍, 단순한 선, 평면적 색감, 따뜻하고 소박한 분위기' },
      { id: 'pixar_3d', label: '픽사 3D 풍', prompt: '고퀄리티 3D 애니메이션 렌더, 부드러운 라이팅, 디테일한 재질감, 따뜻한 색조' },
      { id: 'watercolor', label: '수채화 일러스트', prompt: '수채화 일러스트풍, 번짐과 질감이 보이는 붓터치, 부드러운 파스텔 톤' },
      { id: 'cyberpunk', label: '사이버펑크 네온', prompt: '사이버펑크 네온 조명, 강한 대비, 미래 도시의 분위기, 고채도 컬러' },
      { id: 'minimal', label: '미니멀 그래픽', prompt: '미니멀한 그래픽 포스터 스타일, 단순한 형태, 넓은 여백, 세련된 컬러 팔레트' },
    ]),
    []
  );

  const [generated, setGenerated] = useState({
    dataUrl: '',
    mime: 'image/png',
  });

  const reset = useCallback(() => {
    setPromptMode('recommend');
    setStep(1);
    setIsLoading(false);
    setError('');
    setPersonas([]);
    setIsPersonasLoading(false);
    setSelectedPersonaIds([]);
    setSuggestions([]);
    setSelectedIndex(0);
    setEditedPrompt('');
    setGenerated({ dataUrl: '', mime: 'image/png' });
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
  }, [open, reset]);

  const loadPersonas = useCallback(async () => {
    setIsPersonasLoading(true);
    try {
      const { data: u } = await supabase.auth.getUser();
      const uid = u?.user?.id;
      if (!uid) {
        setPersonas([]);
        return;
      }

      const { data, error: qErr } = await supabase
        .from('ch_personas')
        .select('id, name, image_path, created_at')
        .eq('user_id', uid)
        .order('created_at', { ascending: false });

      if (qErr) {
        setPersonas([]);
        return;
      }

      const vm = await Promise.all(
        (data || []).map(async (r) => {
          const url = r.image_path ? await getCachedSignedUrl({ bucket: 'persona-images', path: r.image_path }) : '';
          return {
            id: r.id,
            name: r.name,
            imagePath: r.image_path,
            imageUrl: url,
            createdAt: r.created_at,
          };
        })
      );

      setPersonas(vm);
    } finally {
      setIsPersonasLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (promptMode !== 'persona') return;
    loadPersonas();
  }, [open, loadPersonas, promptMode]);

  const loadSuggestions = useCallback(async ({ personaNames } = {}) => {
    setIsLoading(true);
    setError('');

    try {
      const { data, error: fnError } = await supabase.functions.invoke('ai-project-cover-suggest', {
        body: { title, description, kind, personaNames },
      });

      if (fnError) {
        setError(fnError?.message || '프롬프트 추천에 실패했습니다');
        return;
      }

      const list = Array.isArray(data?.suggestions) ? data.suggestions : [];
      if (list.length === 0) {
        setError('추천 프롬프트를 가져오지 못했습니다');
        return;
      }

      setSuggestions(list.slice(0, 10));
      setSelectedIndex(0);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setIsLoading(false);
    }
  }, [title, description, kind]);

  useEffect(() => {
    if (!open) return;
    setEditedPrompt(selectedSuggestion);
    setSelectedStyle('');
  }, [open, selectedSuggestion]);

  const applyStyleToPrompt = useCallback((rawPrompt, styleText) => {
    const base = String(rawPrompt || '')
      .replace(/^\s*스타일\s*:\s*.*$/gim, '')
      .trim();

    const styleLine = String(styleText || '').trim();
    if (!styleLine) return base;
    return `${base}\n\n스타일: ${styleLine}`.trim();
  }, []);

  const canProceedFromStep1 = useMemo(() => {
    if (!selectedSuggestion) return false;
    if (promptMode === 'persona' && (selectedPersonaIds?.length || 0) < 1) return false;
    return true;
  }, [promptMode, selectedPersonaIds, selectedSuggestion]);

  const canGenerateSuggestions = useMemo(() => {
    if (!title?.trim() || !description?.trim()) return false;
    if (promptMode === 'persona' && (selectedPersonaIds?.length || 0) < 1) return false;
    return true;
  }, [description, promptMode, selectedPersonaIds, title]);

  const handleGenerateSuggestionsClick = useCallback(async () => {
    if (!canGenerateSuggestions) return;

    if (promptMode === 'persona') {
      const names = (selectedPersonas || []).map((p) => p?.name).filter(Boolean);
      await loadSuggestions({ personaNames: names });
      return;
    }

    await loadSuggestions();
  }, [canGenerateSuggestions, loadSuggestions, promptMode, selectedPersonas]);

  const generateImage = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const { data, error: fnError } = await supabase.functions.invoke('ai-project-cover-generate', {
        body: {
          prompt: editedPrompt,
          referenceImageUrls: promptMode === 'persona'
            ? (selectedPersonas || []).map((p) => p?.imageUrl).filter(Boolean)
            : [],
        },
      });

      if (fnError) {
        setError(fnError?.message || '이미지 생성에 실패했습니다');
        return;
      }

      if (!data?.b64) {
        setError('이미지 데이터가 비어 있습니다');
        return;
      }

      const mime = data?.mime || 'image/png';
      const dataUrl = makeDataUrl(data.b64, mime);
      setGenerated({ dataUrl, mime });
      setStep(3);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setIsLoading(false);
    }
  }, [editedPrompt, promptMode, selectedPersonas]);

  const applyToCover = useCallback(() => {
    if (!generated?.dataUrl) return;

    const file = (() => {
      const parts = String(generated.dataUrl).split(',');
      const header = parts[0] || '';
      const b64 = parts[1] || '';
      const mimeMatch = header.match(/data:(.*?);base64/);
      const mime = mimeMatch ? mimeMatch[1] : 'image/png';
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: mime });
      return new File([blob], 'ai-cover.png', { type: mime });
    })();

    onApply?.(generated.dataUrl, file);
    onClose?.();
  }, [generated, onApply, onClose]);

  if (!open) return null;

  const footerActions = (() => {
    if (step === 1) {
      return (
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="default"
            iconName="ChevronRight"
            iconPosition="right"
            onClick={() => setStep(2)}
            disabled={isLoading || !canProceedFromStep1}
          >
            다음
          </Button>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="outline"
            iconName="ChevronLeft"
            iconPosition="left"
            onClick={() => setStep(1)}
            disabled={isLoading}
          >
            이전
          </Button>

          <Button
            variant="default"
            iconName="Sparkles"
            iconPosition="left"
            onClick={generateImage}
            loading={isLoading}
            disabled={isLoading || !editedPrompt?.trim()}
          >
            생성
          </Button>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between gap-3">
        <Button
          variant="outline"
          iconName="ChevronLeft"
          iconPosition="left"
          onClick={() => setStep(2)}
          disabled={isLoading}
        >
          이전
        </Button>

        <Button
          variant="default"
          iconName="Check"
          iconPosition="left"
          onClick={applyToCover}
          disabled={isLoading || !generated?.dataUrl}
        >
          커버에 적용
        </Button>
      </div>
    );
  })();

  return (
    <div
      className="fixed inset-0 z-dialog bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => (isLoading ? null : onClose?.())}
    >
      <div
        className="relative bg-card rounded-lg shadow-gaming-lg w-full max-w-3xl border border-primary/30 max-h-[90vh] h-[90vh] md:h-auto flex flex-col"
        onClick={(e) => e?.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-card p-4 md:p-6 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-lg md:text-xl font-heading font-semibold text-foreground">AI 커버 이미지 생성</div>
            <div className="text-xs md:text-sm text-muted-foreground caption mt-1">
              1) 프롬프트 추천  2) 프롬프트 수정  3) 이미지 생성
            </div>
          </div>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            닫기
          </Button>
        </div>

        <div className="p-4 md:p-6 flex-1 overflow-y-auto">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setPromptMode('recommend');
                setSuggestions([]);
                setSelectedIndex(0);
                setEditedPrompt('');
                setError('');
              }}
              className={`px-3 py-2 rounded-md text-sm font-medium border transition-gaming ${
                promptMode === 'recommend'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-border hover:bg-muted'
              }`}
            >
              추천 프롬프트
            </button>
            <button
              type="button"
              onClick={() => {
                setPromptMode('persona');
                setSuggestions([]);
                setSelectedIndex(0);
                setEditedPrompt('');
                setError('');
              }}
              className={`px-3 py-2 rounded-md text-sm font-medium border transition-gaming ${
                promptMode === 'persona'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-border hover:bg-muted'
              }`}
            >
              인물 합성 프롬프트
            </button>
          </div>

          {promptMode !== 'persona' ? (
            <div className="mt-3">
              <Button
                variant="outline"
                iconName="Sparkles"
                iconPosition="left"
                onClick={handleGenerateSuggestionsClick}
                loading={isLoading}
                disabled={!canGenerateSuggestions || isLoading}
              >
                추천 프롬프트 생성
              </Button>
            </div>
          ) : null}

          {(!title?.trim() || !description?.trim()) && (
            <div className="mt-3 flex items-start space-x-2 p-3 bg-warning/10 rounded-md border border-warning/20">
              <Icon name="AlertTriangle" size={16} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
              <p className="text-xs md:text-sm text-warning">
                {kind === 'challenge' ? '챌린지 제목과 챌린지 설명을 먼저 입력해주세요.' : '목표 제목과 목표 설명을 먼저 입력해주세요.'}
              </p>
            </div>
          )}

          {error ? (
            <div className="mt-4 flex items-start space-x-2 p-3 bg-destructive/10 rounded-md border border-destructive/20">
              <Icon name="AlertCircle" size={16} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
              <p className="text-xs md:text-sm text-destructive">{error}</p>
            </div>
          ) : null}

          {step === 1 ? (
            <div className="mt-4">
              {promptMode === 'persona' ? (
                <div className="mb-4 rounded-lg border border-border bg-background p-4">
                  <div className="text-sm font-heading font-semibold text-foreground">합성할 인물 선택</div>
                  <div className="text-xs text-muted-foreground caption mt-1">
                    이미지 생성 관리에서 등록한 인물 중 하나를 선택하세요
                  </div>

                  {isPersonasLoading ? (
                    <div className="mt-3 text-xs text-muted-foreground caption">인물 목록 로딩 중…</div>
                  ) : personas.length === 0 ? (
                    <div className="mt-3 flex items-start space-x-2 p-3 bg-warning/10 rounded-md border border-warning/20">
                      <Icon name="AlertTriangle" size={16} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs md:text-sm text-warning">등록된 인물이 없습니다. 먼저 프로필의 '이미지 생성 관리'에서 인물을 추가해주세요.</p>
                        <div className="mt-3">
                          <Button
                            variant="outline"
                            iconName="ExternalLink"
                            iconPosition="left"
                            onClick={() => {
                              navigate('/user-profile-dashboard', { state: { activeTab: 'image_settings' } });
                              onClose?.();
                            }}
                          >
                            이미지 생성 관리로 이동
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {personas.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setSelectedPersonaIds((prev) => {
                              const exists = prev.includes(p.id);
                              return exists ? prev.filter((x) => x !== p.id) : [...prev, p.id];
                            });
                            setSuggestions([]);
                            setSelectedIndex(0);
                            setEditedPrompt('');
                          }}
                          className={`rounded-md border overflow-hidden text-left transition-gaming ${
                            selectedPersonaIds.includes(p.id)
                              ? 'border-primary bg-primary/10'
                              : 'border-border bg-background hover:bg-muted/30'
                          }`}
                        >
                          <div className="relative aspect-video bg-muted">
                            {p.imageUrl ? (
                              <img src={p.imageUrl} alt={p.name} className="w-full h-full object-cover" />
                            ) : null}

                            <div className="absolute top-2 left-2">
                              <div
                                className={`w-5 h-5 rounded-full border flex items-center justify-center bg-background/80 ${
                                  selectedPersonaIds.includes(p.id)
                                    ? 'border-primary'
                                    : 'border-border'
                                }`}
                                aria-hidden="true"
                              >
                                {selectedPersonaIds.includes(p.id) ? (
                                  <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          <div className="p-2">
                            <div className="text-xs font-medium text-foreground truncate">{p.name}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {selectedPersonaIds.length > 0 ? (
                    <div className="mt-3 text-xs text-muted-foreground caption">
                      선택됨: <span className="font-medium text-foreground">{selectedPersonaIds.length}명</span>
                    </div>
                  ) : null}

                  {selectedPersonaIds.length < 1 ? (
                    <div className="mt-3 flex items-start space-x-2 p-3 bg-warning/10 rounded-md border border-warning/20">
                      <Icon name="AlertTriangle" size={16} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                      <p className="text-xs md:text-sm text-warning">인물 합성 프롬프트는 최소 1명의 인물을 선택해야 합니다.</p>
                    </div>
                  ) : null}

                  <div className="mt-3">
                    <Button
                      variant="outline"
                      iconName="Sparkles"
                      iconPosition="left"
                      onClick={handleGenerateSuggestionsClick}
                      loading={isLoading}
                      disabled={!canGenerateSuggestions || isLoading}
                    >
                      인물 합성 프롬프트 추천 생성
                    </Button>
                  </div>
                </div>
              ) : null}

              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <div className="text-sm font-heading font-semibold text-foreground">추천 프롬프트</div>
                  <div className="text-xs text-muted-foreground caption mt-1">10개 중 하나를 선택하세요</div>
                </div>
                <Button
                  variant="outline"
                  iconName="RefreshCw"
                  iconPosition="left"
                  onClick={handleGenerateSuggestionsClick}
                  loading={isLoading}
                  disabled={!canGenerateSuggestions || isLoading}
                >
                  다시 생성
                </Button>
              </div>

              <div className="space-y-2">
                {(suggestions || []).map((s, idx) => (
                  <button
                    key={`${idx}-${String(s).slice(0, 12)}`}
                    type="button"
                    onClick={() => setSelectedIndex(idx)}
                    className={`w-full text-left p-3 rounded-md border transition-gaming ${
                      idx === selectedIndex
                        ? 'border-primary bg-primary text-primary-foreground shadow-gaming-sm'
                        : 'border-border bg-background hover:bg-muted/30'
                    }`}
                  >
                    <div className={`text-xs md:text-sm whitespace-pre-line ${idx === selectedIndex ? 'text-primary-foreground' : 'text-foreground'}`}>{s}</div>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="mt-4">
              <div className="text-sm font-heading font-semibold text-foreground">선택한 프롬프트 수정</div>
              <div className="text-xs text-muted-foreground caption mt-1">원하면 문장을 수정한 후 생성하세요</div>

              <div className="mt-4 rounded-lg border border-border bg-background p-4">
                <div className="text-sm font-heading font-semibold text-foreground">스타일 선택</div>
                <div className="text-xs text-muted-foreground caption mt-1">선택하면 프롬프트에 스타일이 추가됩니다</div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {stylePresets.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => {
                        setSelectedStyle(s.id);
                        setEditedPrompt((prev) => applyStyleToPrompt(prev, s.prompt));
                      }}
                      className={`px-3 py-2 rounded-md text-xs md:text-sm font-medium border transition-gaming ${
                        selectedStyle === s.id
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-foreground border-border hover:bg-muted'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedStyle('');
                      setEditedPrompt((prev) => applyStyleToPrompt(prev, ''));
                    }}
                    className="px-3 py-2 rounded-md text-xs md:text-sm font-medium border border-border bg-background text-foreground hover:bg-muted transition-gaming"
                  >
                    스타일 제거
                  </button>
                </div>
              </div>

              <textarea
                value={editedPrompt}
                onChange={(e) => setEditedPrompt(e?.target?.value)}
                rows={6}
                maxLength={800}
                className="w-full mt-3 px-4 py-3 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-gaming resize-none"
              />
            </div>
          ) : null}

          {step === 3 ? (
            <div className="mt-4">
              <div className="text-sm font-heading font-semibold text-foreground">생성된 이미지</div>
              <div className="text-xs text-muted-foreground caption mt-1">이 이미지를 커버로 적용할 수 있어요</div>

              <div className="mt-3 w-full aspect-video rounded-lg overflow-hidden bg-muted border border-border">
                {generated?.dataUrl ? (
                  <Image
                    src={generated.dataUrl}
                    alt="AI generated cover image"
                    className="w-full h-full object-cover"
                  />
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        <div className="sticky bottom-0 z-10 bg-card p-4 md:p-6 border-t border-border">
          {footerActions}
        </div>

        {isLoading ? (
          <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
            <div className="bg-card rounded-lg border border-border shadow-gaming-md px-6 py-5 flex items-center space-x-3">
              <Icon name="Loader2" size={18} color="var(--color-primary)" className="animate-spin" />
              <div className="text-sm text-foreground">AI 생성 중...</div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AICoverImageWizard;
